// Maps form values onto the BooleanSearchRequest body using each field's
// configured booleanApiKey. The enum mirrors the Go struct in
// Be_Auth_DevOps models/boolean_search.go, which this frontend cannot change.
export const BOOLEAN_API_KEYS = [
  'query',
  'skills',
  'jobTitle',
  'designation',
  'currentLocation',
  'experience.from',
  'experience.to',
  'workExperienceYears',
  'sourceType',
  // Sent as a pair by a daterangePreset field. The Go BooleanSearchRequest does
  // not declare these yet, so they are accepted and ignored server-side (Go's
  // json.Decode drops unknown keys) until the boolean-search API adds them.
  'createdFrom',
  'createdTo',
];

const NUMERIC_KEYS = new Set(['experience.from', 'experience.to', 'workExperienceYears']);

function setDeep(body, key, value) {
  if (!key.includes('.')) {
    body[key] = value;
    return;
  }
  const [root, leaf] = key.split('.');
  body[root] = { ...(body[root] ?? {}), [leaf]: value };
}

function unwrap(raw) {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw.value ?? raw.label ?? raw.name ?? '';
  }
  return raw;
}

function coerce(key, input) {
  const raw = unwrap(input);

  if (NUMERIC_KEYS.has(key)) {
    // Number('') is 0, and 0 is finite — without this guard an absent value (or
    // an object unwrap that yielded '') would be sent as a real bound of zero.
    if (raw === '' || raw === null || raw === undefined) return undefined;
    const numeric = Number(raw);
    return Number.isFinite(numeric) ? numeric : undefined;
  }

  if (Array.isArray(raw)) {
    const parts = raw
      .map(unwrap)
      .filter((part) => part !== undefined && part !== null && String(part).trim() !== '');
    if (!parts.length) return undefined;
    // sourceType is a comma list server-side; every other string key is parsed
    // as a boolean expression, so multi-select values become an OR group.
    return key === 'sourceType' ? parts.join(',') : parts.join(' OR ');
  }

  const text = String(raw ?? '').trim();
  return text === '' ? undefined : text;
}

export function buildBooleanRequest(values = {}, fields = [], options = {}) {
  const body = {};

  fields.forEach((field) => {
    const apiKey = field.booleanApiKey;
    if (!apiKey) return;

    const raw = values[field.field];
    if (raw === undefined || raw === null || raw === '') return;

    // A composite control (range slider, date range) maps one value tuple onto
    // several request params. A range slider already holds a [from, to] array;
    // a daterangePreset holds {preset, from, to}, so unpack it in the same order
    // as its configured key pair.
    if (Array.isArray(apiKey)) {
      let parts;
      if (Array.isArray(raw)) {
        parts = raw;
      } else if (raw && typeof raw === 'object' && ('from' in raw || 'to' in raw)) {
        parts = [raw.from, raw.to];
      } else {
        parts = [raw];
      }
      apiKey.forEach((key, index) => {
        const value = coerce(key, parts[index]);
        if (value !== undefined) setDeep(body, key, value);
      });
      return;
    }

    const value = coerce(apiKey, raw);
    if (value !== undefined) setDeep(body, apiKey, value);
  });

  if (!body.sourceType) body.sourceType = options.defaultSourceType || 'internal';
  return body;
}
